import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { ArrowLeftIcon, UserIcon } from '@heroicons/react/24/outline'
import { PATTERNS } from '@/utils/validation'
import { 
  useCollo, 
  useCreateCollo, 
  useUpdateCollo,
  useSpedizioni,
  useSpedizione,
} from '@/hooks/useOrdini'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/**
 * Form creazione/modifica collo.
 * 
 * FASE 7.4: Spedizioni Avanzate
 * 
 * Features:
 * - Selezione spedizione e destinatario
 * - Campi peso, dimensioni, valore
 * - Validazioni (peso positivo, dimensioni complete)
 * - Gestione creazione e modifica
 */
export default function ColloForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const { data: collo, isLoading } = useCollo(id)
  const createMutation = useCreateCollo()
  const updateMutation = useUpdateCollo()

  const [searchSpedizione, setSearchSpedizione] = useState('')
  const [debouncedSearchSpedizione, setDebouncedSearchSpedizione] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      spedizione: '',
      destinatario: '',
      numero_collo: 1,
      peso_kg: '',
      lunghezza_cm: '',
      larghezza_cm: '',
      altezza_cm: '',
      valore_dichiarato_euro: '',
      note: '',
    },
  })

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchSpedizione(searchSpedizione.trim()), 300)
    return () => clearTimeout(timer)
  }, [searchSpedizione])

  const { data: spedizioniData, isFetching: isSearchingSpedizioni } = useSpedizioni({
    search: debouncedSearchSpedizione || undefined,
  })
  const spedizioneId = Number.parseInt(watch('spedizione'), 10) || null
  const { data: spedizioneSelezionata } = useSpedizione(spedizioneId)
  const spedizioniRisultato = Array.isArray(spedizioniData)
    ? spedizioniData
    : (spedizioniData?.results || [])
  const spedizioni = spedizioneSelezionata && !spedizioniRisultato.some((s) => s.id === spedizioneSelezionata.id)
    ? [spedizioneSelezionata, ...spedizioniRisultato]
    : spedizioniRisultato

  // Popola form in modifica
  useEffect(() => {
    if (collo) {
      reset({
        spedizione: collo.spedizione?.toString() || '',
        destinatario: collo.destinatario?.id?.toString() || '',
        numero_collo: collo.numero_collo || 1,
        peso_kg: collo.peso_kg || '',
        lunghezza_cm: collo.lunghezza_cm || '',
        larghezza_cm: collo.larghezza_cm || '',
        altezza_cm: collo.altezza_cm || '',
        valore_dichiarato_euro: collo.valore_dichiarato_cents != null
          ? (collo.valore_dichiarato_cents / 100).toFixed(2)
          : '',
        note: collo.note || '',
      })
    }
  }, [collo, reset])

  const onSubmit = (data) => {
    // eslint-disable-next-line no-unused-vars
    const { valore_dichiarato_euro, ...rest } = data
    const payload = {
      ...rest,
      spedizione: parseInt(data.spedizione),
      destinatario: parseInt(data.destinatario),
      numero_collo: parseInt(data.numero_collo),
      peso_kg: parseFloat(data.peso_kg),
      lunghezza_cm: data.lunghezza_cm ? parseInt(data.lunghezza_cm) : null,
      larghezza_cm: data.larghezza_cm ? parseInt(data.larghezza_cm) : null,
      altezza_cm: data.altezza_cm ? parseInt(data.altezza_cm) : null,
      valore_dichiarato_cents: valore_dichiarato_euro
        ? Math.round(parseFloat(valore_dichiarato_euro) * 100)
        : 0,
    }

    const mutation = isEdit ? updateMutation : createMutation
    const mutationPayload = isEdit ? { id, data: payload } : payload

    mutation.mutate(mutationPayload, {
      onSuccess: () => {
        navigate('/ordini/colli')
      },
    })
  }

  if (isLoading && isEdit) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Caricamento...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/ordini/colli')}
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? 'Modifica Collo' : 'Nuovo Collo'}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? 'Aggiorna i dati del collo' : 'Crea un nuovo collo per una spedizione'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Spedizione e Destinatario</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Spedizione */}
            <div className="space-y-2">
              <Label htmlFor="spedizione">
                Spedizione <span className="text-destructive">*</span>
              </Label>
              <Input
                value={searchSpedizione}
                onChange={(event) => setSearchSpedizione(event.target.value)}
                placeholder="Cerca per ordine, codice, tracking o cliente..."
              />
              <Select
                value={watch('spedizione')}
                onValueChange={(value) => setValue('spedizione', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona spedizione" />
                </SelectTrigger>
                <SelectContent>
                  {spedizioni.map((spedizione) => (
                    <SelectItem key={spedizione.id} value={spedizione.id.toString()}>
                      {spedizione.ordine_numero || `Spedizione #${spedizione.id}`} - {spedizione.corriere_display}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isSearchingSpedizioni && (
                <p className="text-sm text-muted-foreground">Ricerca spedizioni in corso...</p>
              )}
              {errors.spedizione && (
                <p className="text-sm text-destructive">{errors.spedizione.message}</p>
              )}
            </div>

            {/* Destinatario - Simplified: solo ID numerico per ora */}
            <div className="space-y-2">
              <Label htmlFor="destinatario">
                ID Destinatario <span className="text-destructive">*</span>
              </Label>
              <Input
                id="destinatario"
                type="number"
                {...register('destinatario', {
                  required: 'Il destinatario è obbligatorio',
                })}
                placeholder="1"
              />
              {errors.destinatario && (
                <p className="text-sm text-destructive">{errors.destinatario.message}</p>
              )}
              <p className="text-sm text-muted-foreground">
                Inserisci l'ID del destinatario (vedi lista destinatari)
              </p>
            </div>

            {/* Numero Collo */}
            <div className="space-y-2">
              <Label htmlFor="numero_collo">
                Numero Collo <span className="text-destructive">*</span>
              </Label>
              <Input
                id="numero_collo"
                type="number"
                min="1"
                {...register('numero_collo', {
                  required: 'Il numero collo è obbligatorio',
                  min: { value: 1, message: 'Il numero deve essere almeno 1' },
                })}
              />
              {errors.numero_collo && (
                <p className="text-sm text-destructive">{errors.numero_collo.message}</p>
              )}
              <p className="text-sm text-muted-foreground">
                Numero progressivo del collo nella spedizione (1, 2, 3...)
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dimensioni e Peso</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Peso */}
            <div className="space-y-2">
              <Label htmlFor="peso_kg">
                Peso (kg) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="peso_kg"
                type="number"
                step="0.01"
                min="0.01"
                {...register('peso_kg', {
                  required: 'Il peso è obbligatorio',
                  min: { value: 0.01, message: 'Il peso deve essere maggiore di zero' },
                })}
                placeholder="2.5"
              />
              {errors.peso_kg && (
                <p className="text-sm text-destructive">{errors.peso_kg.message}</p>
              )}
            </div>

            {/* Dimensioni */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lunghezza_cm">Lunghezza (cm)</Label>
                <Input
                  id="lunghezza_cm"
                  type="number"
                  min="1"
                  {...register('lunghezza_cm')}
                  placeholder="30"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="larghezza_cm">Larghezza (cm)</Label>
                <Input
                  id="larghezza_cm"
                  type="number"
                  min="1"
                  {...register('larghezza_cm')}
                  placeholder="20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="altezza_cm">Altezza (cm)</Label>
                <Input
                  id="altezza_cm"
                  type="number"
                  min="1"
                  {...register('altezza_cm')}
                  placeholder="15"
                />
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Se specifichi le dimensioni, devono essere tutte valorizzate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Valore e Note</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Valore Dichiarato */}
            <div className="space-y-2">
              <Label htmlFor="valore_dichiarato_euro">Valore Dichiarato (€)</Label>
              <Input
                id="valore_dichiarato_euro"
                type="number"
                min="0"
                step="0.01"
                {...register('valore_dichiarato_euro')}
                placeholder="50.00"
              />
              <p className="text-sm text-muted-foreground">
                Valore dichiarato per assicurazione
              </p>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label htmlFor="note">Note</Label>
              <Textarea
                id="note"
                {...register('note')}
                placeholder='Es: "Fragile", "Deperibile"'
                rows={3}
              />
              <p className="text-sm text-muted-foreground">
                Note specifiche per questo collo
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Azioni */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/ordini/colli')}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Salvataggio...'
              : isEdit
              ? 'Aggiorna'
              : 'Crea Collo'}
          </Button>
        </div>
      </form>
    </div>
  )
}
